/**
 * FIRST-PARTY ANALYTICS — the client tracker (docs/ANALYTICS.md is the design; the server half is server/analytics.js).
 *
 * `track(name, props)` is the whole API every feature calls. It costs an array push: events wait in an in-memory
 * batch that is POSTed to `/api/a` every FLUSH_MS, when the tab is hidden, and on `pagehide` — never synchronously,
 * never per frame. Hard caps (per minute and per session) mean a runaway loop calling `track` cannot flood the server.
 *
 * IDENTITY. A random VISITOR id lives in localStorage; the SESSION id lives there too, with its last-seen time, and
 * rolls over after SESSION_IDLE_MS without an event or input — so a reload or a second tab continues the visit rather
 * than starting another. The signed-in player is attached by the SERVER from `authHeaders()` (sim/save.ts), which
 * also carries the tutorial test save's header (so its traffic is tagged 'sandbox'); an admin's is tagged 'admin'
 * from the session. The player id is never put in the body.
 *
 * OFF ENTIRELY when the browser sends Do Not Track or Global Privacy Control, when the player opted out (Advanced
 * Settings → `setAnalyticsOptOut`), and in a build with no server (`cloudSaveEnabled` false — local dev, static
 * builds). Off means no ids written, no listeners, no requests. Dev can force it on for the smoke test (FORCE_KEY).
 *
 * UNLOAD. `pagehide` sends the rest of the batch plus `session_end`. An anonymous visitor's goes by
 * `navigator.sendBeacon` (a beacon can carry no headers, and an anonymous batch needs none); a signed-in or sandbox
 * batch needs its headers, so it goes as a `keepalive` fetch with the headers the last flush resolved (a Clerk
 * token cannot be awaited during unload). Either way it shares the browser's 64 KiB unload quota with the save's
 * own keepalive PUT (save.ts KEEPALIVE_MAX), so the unload batch is capped at UNLOAD_MAX_EVENTS — a few hundred bytes.
 */
import { authHeaders, cloudSaveEnabled } from "./save";
import { SANDBOX_HEADER } from "./sandbox";

/**
 * THE CATALOGUE, typed. Must match `ANALYTICS_EVENTS` in server/analytics.js — the server drops any event or prop it
 * does not declare. Adding an event = a line there, a line here, and a row in docs/ANALYTICS.md.
 */
export interface AnalyticsEvents {
  page_view: { title?: string };
  scene_view: { scene: string };
  game_open: { loadMs: number; returning: boolean };
  cta_click: { id: string };
  auth_prompt: { mode: "sign_in" | "sign_up" };
  auth_done: { mode: "sign_in" | "sign_up" };
  checkout_start: { product: string; kind: "item" | "club" | "petals" };
  panel_open: { id: "premium_shop" | "garden_club" | "petals" | "kickstarter" };
  product_view: { product: string };
  access_code: { ok: boolean };
  /** The game's per-session counters (sim/playCounts.ts): DELTAS since the last play_counts sent, zeros left out. */
  play_counts: Partial<Record<PlayCountKey, number>>;
  /** Technical health (sim/clientHealth.ts): one per distinct error per page load. */
  client_error: { kind: "error" | "rejection" | "react" | "webgl"; msg: string; frame: string; build: string; stack?: string };
  /** Technical health (sim/clientHealth.ts): the game's performance, once per page load. */
  client_perf: {
    firstSceneMs: number; fromNav: boolean; loadGarden?: number; loadGreenhouse?: number; loadOther?: number;
    cpuMs?: number; frames?: number; dpr: number; lowGfx: boolean; device: "desktop" | "tablet" | "mobile"; build: string;
  };
}
/** The counters `play_counts` may carry — one prop each on the server. */
export type PlayCountKey =
  | "harvests" | "sown" | "fishCast" | "fishCaught" | "fishLost" | "bugsCaught" | "bugsMissed" | "decorPlaced"
  | "plotsBought" | "waterStrokes" | "terrainStrokes" | "achievements" | "lvlGarden" | "lvlFish" | "lvlBug"
  | "checkins" | "sceneSwitches" | "msGreenhouse" | "msGarden" | "msOther";
/** Sent by this module itself, never by a caller. */
interface InternalEvents {
  session_start: {
    entry: "site" | "game"; referrer?: string; utm_source?: string; utm_medium?: string; utm_campaign?: string;
    screen?: ScreenBucket; touch?: boolean;
  };
  session_end: { activeMs: number };
}

const URL_A = "/api/a";
const FLUSH_MS = 20_000;
const SESSION_IDLE_MS = 30 * 60_000;
/** No input for this long and active time stops accruing (matches the usage heartbeat, sim/stats.ts). */
const INPUT_IDLE_MS = 60_000;
/** A runaway loop's ceiling: past these, `track` drops the event (session_start / session_end are exempt). */
const MAX_PER_MINUTE = 30;
const MAX_PER_SESSION = 500;
/** The server reads at most 50 per request. */
const MAX_BATCH = 50;
/** Events held when the server cannot be reached; the oldest go first. */
const MAX_QUEUE = 100;
const UNLOAD_MAX_EVENTS = 20;
/** A Clerk session token lives 60 s; a snapshot older than this is not sent at unload. */
const HEADER_FRESH_MS = 45_000;
/** A send the server has not answered by now is abandoned (and re-queued): a hung keepalive request would otherwise
 *  keep `sending` set — no more sends — and hold its bytes of the browser's 64 KiB keepalive quota, which the save's
 *  unload PUT shares. The server answers in about a millisecond (docs/ANALYTICS.md, *Load test*). */
const SEND_TIMEOUT_MS = 10_000;

const VISITOR_KEY = "shelf-garden.analytics.visitor";
const SESSION_KEY = "shelf-garden.analytics.session";
const OPT_OUT_KEY = "shelf-garden.analytics.optOut";
/** DEV ONLY: "1" turns tracking on in a build with no server, so scripts/smoke.mjs can watch the batches. */
const FORCE_KEY = "shelf-garden.analytics.force";

interface Queued { n: string; p?: object; pg: string; t: number; s: string }
interface Session { id: string; last: number; activeMs: number }

function store(): Storage | null { try { return localStorage; } catch { return null; } }
function newId(): string {
  return crypto.randomUUID?.() ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

/** The browser asked not to be tracked (Do Not Track, or Global Privacy Control). */
export function privacySignal(): boolean {
  if (typeof navigator === "undefined") return false;
  const n = navigator as Navigator & { globalPrivacyControl?: boolean; msDoNotTrack?: string };
  const w = typeof window !== "undefined" ? (window as Window & { doNotTrack?: string }) : undefined;
  return n.doNotTrack === "1" || n.doNotTrack === "yes" || n.msDoNotTrack === "1" || w?.doNotTrack === "1" || n.globalPrivacyControl === true;
}
export function analyticsOptedOut(): boolean { return store()?.getItem(OPT_OUT_KEY) === "1"; }

function allowed(): boolean {
  if (privacySignal() || analyticsOptedOut()) return false;
  if (cloudSaveEnabled) return true;
  return import.meta.env.DEV && store()?.getItem(FORCE_KEY) === "1";
}

// ── state (all of it lazily set up by the first `track`) ──────────────────────────────────────────────────────────
let on: boolean | null = null;
let queue: Queued[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let sending = false;
let headerSnap: { h: Record<string, string>; at: number } | null = null;
let lastInput = 0;
let lastAccrue = 0;
let visible = true;
let minuteStart = 0, minuteCount = 0;
let sessionCount = 0, countedSession = "";
let lastPageView = "";
let firstSessionOfPage = true;

function now(): number { return Date.now(); }

function readSession(): Session | null {
  try {
    const s = JSON.parse(store()?.getItem(SESSION_KEY) || "null") as Session | null;
    return s && typeof s.id === "string" && typeof s.last === "number" ? { id: s.id, last: s.last, activeMs: Number(s.activeMs) || 0 } : null;
  } catch { return null; }
}
let memSession: Session | null = null;      // storage blocked → this page's own session
function writeSession(s: Session): void {
  memSession = s;
  try { store()?.setItem(SESSION_KEY, JSON.stringify(s)); } catch { /* storage full or blocked */ }
}
function visitorId(): string {
  const st = store();
  let v = st?.getItem(VISITOR_KEY) ?? null;
  if (!v) { v = newId(); try { st?.setItem(VISITOR_KEY, v); } catch { /* ephemeral */ } }
  return v;
}

/** The live session, minting one (and its session_start) when there is none or the last went idle. */
function session(): Session {
  const t = now();
  const first = firstSessionOfPage;
  firstSessionOfPage = false;
  const s = readSession() ?? memSession;
  if (s && t - s.last < SESSION_IDLE_MS) return s;
  const fresh: Session = { id: newId(), last: t, activeMs: 0 };
  writeSession(fresh);
  enqueue({ n: "session_start", p: startProps(first), pg: location.pathname, t, s: fresh.id });
  return fresh;
}

/** The CSS screen width, bucketed — the same cuts as server/analytics.js SCREEN_BUCKETS. Never the exact size. */
type ScreenBucket = "<480" | "480-767" | "768-1023" | "1024-1439" | "1440-1919" | "1920+";
function screenBucket(): ScreenBucket | undefined {
  const w = typeof screen !== "undefined" ? screen.width : 0;
  if (!w) return undefined;
  return w < 480 ? "<480" : w < 768 ? "480-767" : w < 1024 ? "768-1023" : w < 1440 ? "1024-1439" : w < 1920 ? "1440-1919" : "1920+";
}

/** Landing context: where the visit began (the event's page), the referring HOST (never the URL) and the UTM tags;
 *  and the device's screen-width bucket and whether it has touch, for the admin Visitors page's DEVICES panel. */
function startProps(first: boolean): InternalEvents["session_start"] {
  const p: InternalEvents["session_start"] = { entry: location.pathname.startsWith("/play") ? "game" : "site" };
  const sb = screenBucket();
  if (sb) p.screen = sb;
  p.touch = (navigator.maxTouchPoints ?? 0) > 0;
  if (!first) return p;        // a rollover mid-page has no new referrer or campaign
  try {
    if (document.referrer) {
      const host = new URL(document.referrer).host;
      if (host && host !== location.host) p.referrer = host;
    }
  } catch { /* unparsable referrer: leave it out */ }
  const q = new URLSearchParams(location.search);
  for (const k of ["utm_source", "utm_medium", "utm_campaign"] as const) {
    const v = q.get(k);
    if (v) p[k] = v.slice(0, 64);
  }
  return p;
}

/** Fold the active time since the last call into the session: visible, and some input in the last minute. */
function accrue(): void {
  const t = now();
  const from = lastAccrue;
  lastAccrue = t;
  if (!visible || !from) return;
  const until = Math.min(t, lastInput + INPUT_IDLE_MS);
  const s = readSession() ?? memSession;
  if (!s || t - s.last >= SESSION_IDLE_MS) return;
  if (until > from) s.activeMs += until - from;
  if (lastInput > from) s.last = t;        // input keeps the session alive even with no events
  writeSession(s);
}

const onInput = (): void => { lastInput = now(); };
const onVisibility = (): void => {
  accrue();
  visible = document.visibilityState !== "hidden";
  if (visible) lastInput = now();
  else { contribute(true); void flush(); }
};
/**
 * Batched producers (sim/playCounts.ts) hand over what they have been counting just before a send. `final` is true
 * when the tab is hidden or the page is going away (send what you have — there may be no later chance), false on the
 * periodic tick (a producer may wait for more). A contributor calls `track` itself, and only when it has news.
 */
const contributors = new Set<(final: boolean) => void>();
export function onAnalyticsFlush(fn: (final: boolean) => void): () => void {
  contributors.add(fn);
  return () => { contributors.delete(fn); };
}
function contribute(final: boolean): void {
  for (const fn of contributors) { try { fn(final); } catch { /* a counter must never break the send */ } }
}

const onPageHide = (): void => {
  accrue();
  contribute(true);
  const s = readSession() ?? memSession;
  if (s) queue.push({ n: "session_end", p: { activeMs: Math.round(s.activeMs) }, pg: location.pathname, t: now(), s: s.id });
  sendNow();
};
const onPageShow = (e: PageTransitionEvent): void => { if (e.persisted) { lastAccrue = now(); lastInput = now(); } };

function start(): boolean {
  if (on !== null) return on;
  on = allowed();
  if (!on) return false;
  visitorId();
  visible = document.visibilityState !== "hidden";
  lastInput = lastAccrue = now();
  for (const ev of ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"]) window.addEventListener(ev, onInput, { passive: true });
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("pageshow", onPageShow);
  timer = setInterval(() => { accrue(); contribute(false); void flush(); }, FLUSH_MS);
  return true;
}

function stop(): void {
  if (timer) clearInterval(timer);
  timer = null;
  for (const ev of ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"]) window.removeEventListener(ev, onInput);
  document.removeEventListener("visibilitychange", onVisibility);
  window.removeEventListener("pagehide", onPageHide);
  window.removeEventListener("pageshow", onPageShow);
  queue = [];
  on = null;
}

/** Record one event. Cheap and synchronous — it only queues; nothing is sent from here. */
export function track<K extends keyof AnalyticsEvents>(name: K, props: AnalyticsEvents[K], page: string = location.pathname): void {
  if (!start()) return;
  const t = now();
  if (t - minuteStart >= 60_000) { minuteStart = t; minuteCount = 0; }
  if (minuteCount >= MAX_PER_MINUTE) return;      // before any storage work: a runaway loop costs a compare
  const s = session();
  if (countedSession !== s.id) { countedSession = s.id; sessionCount = 0; }
  if (sessionCount >= MAX_PER_SESSION) return;
  minuteCount++; sessionCount++;
  s.last = t;
  writeSession(s);
  enqueue({ n: name, p: props, pg: page, t, s: s.id });
}
function enqueue(e: Queued): void {
  queue.push(e);
  if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
}

/** A route was shown (site router and /play). Consecutive repeats of one path are one view (StrictMode re-runs). */
export function trackPageView(path: string): void {
  if (path === lastPageView) return;
  lastPageView = path;
  track("page_view", document.title ? { title: document.title.slice(0, 80) } : {}, path);
}

/** The next batch: up to MAX_BATCH events of ONE session (the envelope carries one session id). */
function takeBatch(max: number): { body: string; taken: Queued[] } | null {
  const sid = queue[0]?.s;
  if (!sid) return null;
  let i = 0;
  while (i < queue.length && i < max && queue[i]!.s === sid) i++;
  const taken = queue.splice(0, i);
  const body = JSON.stringify({ v: visitorId(), s: sid, e: taken.map(({ n, p, pg, t }) => ({ n, p, pg, t })) });
  return { body, taken };
}

/** The ordinary flush (timer, tab hidden): fresh headers, a keepalive fetch, re-queued if the server was unreachable. */
async function flush(): Promise<void> {
  if (!on || sending || !queue.length) return;
  sending = true;
  try {
    const h = await authHeaders({ "content-type": "application/json" });
    headerSnap = { h, at: now() };
    const b = takeBatch(MAX_BATCH);
    if (!b) return;
    let ok = false;
    try {
      const signal = typeof AbortSignal.timeout === "function" ? AbortSignal.timeout(SEND_TIMEOUT_MS) : undefined;
      const res = await fetch(URL_A, { method: "POST", headers: h, body: b.body, keepalive: true, signal });
      ok = res.ok || (res.status >= 400 && res.status < 500 && res.status !== 429);   // a refused batch is not retried
    } catch { /* offline, or the server did not answer in time: quietly kept for the next tick */ }
    if (!ok) { queue.unshift(...b.taken); if (queue.length > MAX_QUEUE) queue.length = MAX_QUEUE; }
  } finally {
    sending = false;
  }
}

/** The unload send: synchronous, best effort, one request. */
function sendNow(): void {
  if (queue.length > UNLOAD_MAX_EVENTS) queue.splice(0, queue.length - UNLOAD_MAX_EVENTS);   // keep the newest (session_end)
  const b = takeBatch(UNLOAD_MAX_EVENTS);
  queue = [];
  if (!b) return;
  const h = headerSnap && now() - headerSnap.at < HEADER_FRESH_MS ? headerSnap.h : null;
  if (h && (h.authorization || h[SANDBOX_HEADER])) {
    try { void fetch(URL_A, { method: "POST", headers: h, body: b.body, keepalive: true }).catch(() => {}); } catch { /* quota */ }
    return;
  }
  try { navigator.sendBeacon?.(URL_A, b.body); } catch { /* quota */ }
}

/**
 * The player's switch (Advanced Settings). Opting out stops tracking now, drops what was queued and forgets this
 * browser's visitor and session ids; opting back in starts fresh from the next event.
 */
export function setAnalyticsOptOut(out: boolean): void {
  const st = store();
  try {
    if (out) { st?.setItem(OPT_OUT_KEY, "1"); st?.removeItem(VISITOR_KEY); st?.removeItem(SESSION_KEY); }
    else st?.removeItem(OPT_OUT_KEY);
  } catch { /* storage blocked: nothing was kept anyway */ }
  memSession = null;
  stop();
}
